import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { clientsAPI, projectsAPI, statusesAPI, tasksAPI, usersAPI } from '../services/api';

export default function Dashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('my');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    clientId: '',
    projectId: '',
    assigneeId: '',
    statusId: '',
    deadline: ''
  });
  const { user } = useAuth();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tasksRes, projectsRes, clientsRes, usersRes, statusesRes] = await Promise.all([
        tasksAPI.getAll(),
        projectsAPI.getAll(),
        clientsAPI.getAll(),
        usersAPI.getAll(),
        statusesAPI.getAll()
      ]);
      setTasks(tasksRes.data);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      setUsers(usersRes.data);
      setStatuses(statusesRes.data);
    } catch (err) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredTasks = () => {
    const now = new Date();
    // Фильтруем только задачи текущего пользователя
    let filtered = tasks.filter(t => t.assigneeId === user?.id);

    if (filter === 'today') {
      const today = format(now, 'yyyy-MM-dd');
      filtered = filtered.filter(t => t.deadline && t.deadline.startsWith(today));
    } else if (filter === 'overdue') {
      filtered = filtered.filter(t => t.deadline && new Date(t.deadline) < now);
    }

    return filtered.sort((a, b) => {
      if (!a.deadline) return 1;
      if (!b.deadline) return -1;
      return new Date(a.deadline) - new Date(b.deadline);
    });
  };

  const isOverdue = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  const handleOpenDialog = (task) => {
    setEditingTask(task);
    setFormData({
      description: task.description,
      clientId: task.clientId || '',
      projectId: task.projectId,
      assigneeId: task.assigneeId,
      statusId: task.statusId,
      deadline: task.deadline ? task.deadline.split('T')[0] : ''
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTask(null);
  };

  const normalizeTaskPayload = (raw) => {
    const payload = { ...raw };
    // Преобразуем пустые строки в null для всех полей
    if (payload.clientId === '') payload.clientId = null;
    if (payload.projectId === '') payload.projectId = null;
    if (payload.assigneeId === '') payload.assigneeId = null;
    if (payload.statusId === '') payload.statusId = null;
    if (payload.deadline === '') payload.deadline = null;
    return payload;
  };

  const handleSave = async () => {
    try {
      setError('');
      const payload = normalizeTaskPayload(formData);
      await tasksAPI.update(editingTask.id, payload);
      await loadData();
      handleCloseDialog();
    } catch (err) {
      const data = err?.response?.data;
      const msg = data?.error || (Array.isArray(data?.errors) ? data.errors.map(e => e?.msg).filter(Boolean).join(', ') : '');
      setError(msg ? `Ошибка сохранения: ${msg}` : 'Ошибка сохранения');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredTasks = getFilteredTasks();

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Typography variant={isMobile ? "h6" : "h4"} fontWeight="bold" gutterBottom>
        Главная панель
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* 3 карточки в ряд - кликабельные */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              cursor: 'pointer',
              bgcolor: filter === 'my' ? 'primary.light' : 'white',
              transition: 'all 0.2s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 }
            }}
            onClick={() => setFilter('my')}
          >
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Мои задачи
              </Typography>
              <Typography variant="h3" fontWeight="bold">
                {tasks.filter(t => t.assigneeId === user?.id).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              cursor: 'pointer',
              bgcolor: filter === 'today' ? 'success.light' : 'white',
              transition: 'all 0.2s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 }
            }}
            onClick={() => setFilter('today')}
          >
            <CardContent>
              <Typography variant="h6" color="success.main" gutterBottom>
                На сегодня
              </Typography>
              <Typography variant="h3" fontWeight="bold">
                {tasks.filter(t => t.assigneeId === user?.id && t.deadline && t.deadline.startsWith(format(new Date(), 'yyyy-MM-dd'))).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              cursor: 'pointer',
              bgcolor: filter === 'overdue' ? 'error.light' : 'white',
              transition: 'all 0.2s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 }
            }}
            onClick={() => setFilter('overdue')}
          >
            <CardContent>
              <Typography variant="h6" color="error.main" gutterBottom>
                Просрочено
              </Typography>
              <Typography variant="h3" fontWeight="bold">
                {tasks.filter(t => t.assigneeId === user?.id && t.deadline && new Date(t.deadline) < new Date()).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Список задач по хронологии */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Задачи {filter === 'my' ? 'мои' : filter === 'today' ? 'на сегодня' : 'просроченные'}
          </Typography>

          {isMobile ? (
            <Box display="flex" flexDirection="column" gap={1.5} mt={2}>
              {filteredTasks.map((task) => (
                <Card
                  key={task.id}
                  onClick={() => handleOpenDialog(task)}
                  sx={{
                    bgcolor: isOverdue(task.deadline) ? '#ffebee' : 'white',
                    border: isOverdue(task.deadline) ? '1px solid #ef5350' : 'none',
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 2 }
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                      {task.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {task.client?.companyName || 'Без клиента'} • {task.project?.name} • {task.assignee?.fullName}
                    </Typography>
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                      <Chip
                        label={task.status?.name}
                        size="small"
                        sx={{
                          bgcolor: task.status?.color,
                          color: 'white',
                          height: 20,
                          fontSize: '0.7rem'
                        }}
                      />
                      {task.deadline && (
                        <Chip
                          label={format(new Date(task.deadline), 'dd.MM.yyyy', { locale: ru })}
                          size="small"
                          color={isOverdue(task.deadline) ? 'error' : 'default'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
              {filteredTasks.length === 0 && (
                <Typography color="text.secondary" align="center" py={2}>
                  Нет задач
                </Typography>
              )}
            </Box>
          ) : (
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Описание</TableCell>
                    <TableCell>Клиент</TableCell>
                    <TableCell>Проект</TableCell>
                    <TableCell>Исполнитель</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Дедлайн</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTasks.map((task) => (
                    <TableRow
                      key={task.id}
                      onClick={() => handleOpenDialog(task)}
                      sx={{
                        bgcolor: isOverdue(task.deadline) ? '#ffebee' : 'inherit',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: isOverdue(task.deadline) ? '#ffcdd2' : 'action.hover' }
                      }}
                    >
                      <TableCell sx={{ wordBreak: 'break-word', maxWidth: '300px' }}>{task.description}</TableCell>
                      <TableCell>{task.client?.companyName || '-'}</TableCell>
                      <TableCell>{task.project?.name}</TableCell>
                      <TableCell>{task.assignee?.fullName}</TableCell>
                      <TableCell>
                        <Chip
                          label={task.status?.name}
                          size="small"
                          sx={{
                            bgcolor: task.status?.color,
                            color: 'white'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {task.deadline ? (
                          <Chip
                            label={format(new Date(task.deadline), 'dd.MM.yyyy', { locale: ru })}
                            size="small"
                            color={isOverdue(task.deadline) ? 'error' : 'default'}
                            variant="outlined"
                          />
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredTasks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary">Нет задач</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Диалог редактирования задачи */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Редактировать задачу
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Описание"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              fullWidth
              size="small"
              multiline
              rows={8}
            />
            <TextField
              select
              label="Клиент"
              value={formData.clientId}
              onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
              fullWidth
              size="small"
            >
              <MenuItem value="">Не выбран</MenuItem>
              {clients.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.companyName}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Проект"
              value={formData.projectId}
              onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
              required
              fullWidth
              size="small"
            >
              {projects.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Исполнитель"
              value={formData.assigneeId}
              onChange={(e) => setFormData({ ...formData, assigneeId: e.target.value })}
              required
              fullWidth
              size="small"
            >
              {users.map(u => (
                <MenuItem key={u.id} value={u.id}>{u.fullName}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Статус"
              value={formData.statusId}
              onChange={(e) => setFormData({ ...formData, statusId: e.target.value })}
              required
              fullWidth
              size="small"
            >
              {statuses.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Дедлайн"
              type="date"
              value={formData.deadline}
              onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.description || !formData.projectId}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
